import type { FocusEvent, ReactNode } from "react";
import { useEffect, useId, useMemo, useRef, useState } from "react";

export type MultiSelectOption = {
  key: string;
  label: string;
  // Optional badge gerendert direkt nach dem Label (z. B. "Lager"-Tag).
  badge?: ReactNode;
  // Sekundärer Text im Popover am rechten Rand (z. B. Zeilen-/Ausgaben-Count).
  secondary?: ReactNode;
  // Volltext-Such-Treffer; default ist das Label.
  searchText?: string;
};

type Props = {
  value: string[];
  onChange: (next: string[]) => void;
  options: MultiSelectOption[];
  placeholder?: string;
  isLoading?: boolean;
  emptyHint?: string;
  ariaLabel?: string;
  // Maximale Anzahl Chips, die im Trigger sichtbar sind, bevor zu „+N“ kollabiert wird.
  maxVisibleChips?: number;
  // Maximalanzahl Vorschläge im Popover.
  maxSuggestions?: number;
};

// Kompakte Chip-Combobox. Wird für Hersteller, Projekt und Lieferadresse
// genutzt — alle drei Filter haben damit dieselbe Höhe, Affordanz und
// Tastaturbedienung. Bei vielen Treffern kollabieren ältere Chips in einen
// „+N weitere"-Anzeiger, damit die Filter-Zeile ihre Höhe behält.
export default function MultiSelectChips({
  value,
  onChange,
  options,
  placeholder = "suchen …",
  isLoading = false,
  emptyHint,
  ariaLabel,
  maxVisibleChips = 3,
  maxSuggestions = 50,
}: Props) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const listboxId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const byKey = useMemo(() => {
    const map = new Map<string, MultiSelectOption>();
    for (const option of options) {
      map.set(option.key, option);
    }
    return map;
  }, [options]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const base = needle
      ? options.filter((option) => {
          const haystack = (option.searchText ?? option.label).toLowerCase();
          return haystack.includes(needle) || option.key.toLowerCase().includes(needle);
        })
      : options;
    return base.slice(0, maxSuggestions);
  }, [options, query, maxSuggestions]);

  function toggle(key: string) {
    if (value.includes(key)) {
      onChange(value.filter((k) => k !== key));
    } else {
      onChange([...value, key]);
    }
  }

  const visibleChips = value.slice(0, maxVisibleChips);
  const hiddenCount = Math.max(0, value.length - visibleChips.length);
  const activeIndex = Math.min(
    highlightedIndex,
    Math.max(0, filtered.length - 1),
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [options, query]);

  useEffect(() => {
    if (!open || filtered.length === 0) return;
    optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, filtered.length, open]);

  function handleBlur(event: FocusEvent<HTMLDivElement>) {
    const nextTarget = event.relatedTarget;
    if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
      setOpen(false);
    }
  }

  return (
    <div
      className="relative"
      onFocus={() => setOpen(true)}
      onBlur={handleBlur}
    >
      <div
        className="flex min-h-9 cursor-text flex-wrap items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm shadow-sm focus-within:border-emerald-300 focus-within:ring-2 focus-within:ring-emerald-500/20"
        onClick={(event) => {
          if (
            event.target !== event.currentTarget &&
            !(event.target as HTMLElement).closest('[data-combobox-affordance="true"]')
          ) {
            return;
          }
          inputRef.current?.focus();
          setOpen(true);
        }}
      >
        {visibleChips.map((key) => {
          const option = byKey.get(key);
          return (
            <span
              key={key}
              className="inline-flex max-w-[14rem] items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800"
            >
              <span className="truncate">{option?.label ?? key}</span>
              {option?.badge}
              <button
                type="button"
                onClick={() => toggle(key)}
                className="text-emerald-700 hover:text-emerald-900"
                aria-label={`${option?.label ?? key} entfernen`}
              >
                ×
              </button>
            </span>
          );
        })}
        {hiddenCount > 0 ? (
          <span
            className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600"
            title={value
              .slice(maxVisibleChips)
              .map((key) => byKey.get(key)?.label ?? key)
              .join(", ")}
          >
            +{hiddenCount} weitere
          </span>
        ) : null}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setOpen(false);
              event.preventDefault();
            } else if (event.key === "ArrowDown" && filtered.length > 0) {
              setOpen(true);
              setHighlightedIndex((current) =>
                open ? (current + 1) % filtered.length : 0,
              );
              event.preventDefault();
            } else if (event.key === "ArrowUp" && filtered.length > 0) {
              setOpen(true);
              setHighlightedIndex((current) =>
                open
                  ? (current - 1 + filtered.length) % filtered.length
                  : filtered.length - 1,
              );
              event.preventDefault();
            } else if (event.key === "Enter" && open && filtered.length > 0) {
              toggle(filtered[activeIndex].key);
              event.preventDefault();
            }
          }}
          placeholder={value.length === 0 ? placeholder : ""}
          role="combobox"
          aria-label={ariaLabel ?? placeholder}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={
            open && filtered.length > 0
              ? `${listboxId}-option-${activeIndex}`
              : undefined
          }
          className="min-w-[6rem] flex-1 border-0 bg-transparent px-1 py-0.5 text-sm outline-none"
        />
        <span
          aria-hidden="true"
          data-combobox-affordance="true"
          className={`flex-none text-xs text-slate-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        >
          ▾
        </span>
      </div>
      {open ? (
        <ul
          id={listboxId}
          role="listbox"
          aria-label={ariaLabel ?? placeholder}
          aria-multiselectable="true"
          className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-lg"
        >
          {isLoading ? (
            <li className="px-3 py-2 text-xs text-slate-500">Lade …</li>
          ) : filtered.length === 0 ? (
            <li className="px-3 py-2 text-xs text-slate-500">
              {emptyHint ?? "Keine Treffer."}
            </li>
          ) : (
            filtered.map((option, index) => {
              const selected = value.includes(option.key);
              return (
                <li key={option.key} role="none">
                  <button
                    id={`${listboxId}-option-${index}`}
                    ref={(node) => {
                      optionRefs.current[index] = node;
                    }}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    onMouseDown={(event) => {
                      // mousedown vor blur, damit Klick die Auswahl trifft.
                      event.preventDefault();
                    }}
                    onClick={() => toggle(option.key)}
                    className={`flex w-full items-center justify-between gap-2 px-2 py-1 text-left text-sm hover:bg-slate-50 ${
                      selected
                        ? "bg-emerald-50"
                        : index === activeIndex
                          ? "bg-slate-50"
                          : ""
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-2">
                      <span
                        className={`inline-block h-3 w-3 shrink-0 rounded border ${
                          selected
                            ? "border-emerald-600 bg-emerald-600"
                            : "border-slate-300"
                        }`}
                      />
                      <span className="truncate">{option.label}</span>
                      {option.badge}
                    </span>
                    {option.secondary ? (
                      <span className="shrink-0 text-xs text-slate-500 tabular-nums">
                        {option.secondary}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })
          )}
        </ul>
      ) : null}
    </div>
  );
}
